/**
 * BuffingFormV2 — stock-first, progressive-disclosure Buffing flow (same pattern
 * as JobFormV2). Replaces the old BuffingForm.
 *
 * Flow: stock number → brand + car history → model → BUFFING SCOPE (weighted
 * part selector, live %) → authorized by → detailing by.
 *
 * On submit it does BOTH ("os dois"):
 *   1. buffing_requests authorization (api.submitBuffing) — keeps the admin /
 *      dealership approval email flow.
 *   2. a BUFFING job lançamento (api.submitJob, units = pct/100) — BUFFING is
 *      $200 full / $140 pay, so the % scales the pay.
 *
 * The part list + weights below are a starting proposal — the owner adjusts the
 * weights and sends the Claude Design car diagram, which then replaces the
 * placeholder chip grid in the BUFFING SCOPE section.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import {
  TEAL, TEAL_TINT, NAVY, YELLOW, GREEN, CHECK, PAGE, LINE, TRACK,
  G400, G500, G700, G300, MONO, PUB2, BRAND_LOGO_MAP,
  isNewCarStock, brandFromStock, stockLen, fmtDMY, chip, SectionShell, SegmentedStock, FormHeader,
} from './formKit';

// Weighted parts — sum to 100% (full car). Owner will adjust the weights and
// the diagram from Claude Design replaces the chip grid.
export const BUFFING_PARTS = [
  { key: 'BONNET',      label: 'Bonnet' },
  { key: 'ROOF',        label: 'Roof' },
  { key: 'HEADLIGHTS',  label: 'Head light restoration' },
  { key: 'SIDES_UPPER', label: 'Sides — upper' },
  { key: 'SIDES_LOWER', label: 'Sides — lower' },
  { key: 'FRONT_REAR',  label: 'Front & rear' },
];
const TOTAL_PARTS = BUFFING_PARTS.length;

// The percentage is chosen, not calculated. Adding up per-part weights meant
// bonnet + roof cost the sum of both, when two panels in one visit are less
// work than two separate visits — and no fixed weight can know that this
// particular car is bigger or harder than the last one. So the parts record
// WHAT was done and the percentage says what it was worth.
export const BUFFING_TIERS = [30, 50, 70, 100];

// brandFromStock output → buffing_requests department (matches the dept→email map).
const DEPT_FROM_BRAND = { 'KIA CARS': 'KIA', 'GWM CARS': 'GWM', 'SUZUKI CARS': 'SUZUKI', 'GAC CARS': 'GAC', 'USED CARS': 'USED CARS' };

export default function BuffingFormV2({ user, onSubmitted }) {
  const [carModels, setCarModels] = useState({});
  const [contractors, setContractors] = useState([]);
  // Admin-tunable percentages (Settings → Buffing %); falls back to defaults.
  const [tiers, setTiers] = useState(BUFFING_TIERS);

  const [stockMode, setStockMode] = useState('stock');
  const [stock, setStock] = useState('');
  const [stockDigitLen, setStockDigitLen] = useState(6);
  const [plateDone, setPlateDone] = useState(false);
  const [model, setModel] = useState('');
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const [parts, setParts] = useState(new Set());
  const [pct, setPct] = useState(null);
  const [scopeDone, setScopeDone] = useState(false);
  const [authorizedBy, setAuthorizedBy] = useState('NOAH DAVIES');
  const [authDone, setAuthDone] = useState(false);
  const [employeeIds, setEmployeeIds] = useState(user?.type === 'admin' ? [] : [user?.id]);
  const [detailerDone, setDetailerDone] = useState(false);

  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.getCarModels().then(setCarModels).catch(() => {});
    api.getActiveContractors().then(r => setContractors(Array.isArray(r) ? r : (r.contractors || []))).catch(() => {});
    api.getBuffingTiers().then(r => Array.isArray(r.tiers) && r.tiers.length && setTiers(r.tiers)).catch(() => {});
  }, []);

  const brand = brandFromStock(stock);
  const brandModels = (brand && carModels[brand] ? carModels[brand].map(m => m.model || m) : []).slice().sort();
  const detailers = contractors.filter(c => c.type !== 'office_cleaner');

  const allParts = parts.size === TOTAL_PARTS;

  const lookup = useCallback(async (sn) => {
    const s = String(sn || '').trim();
    if (!s) { setHistory(null); return; }
    setHistLoading(true);
    try {
      const h = await api.getCarHistory(s);
      setHistory(h);
      setModel(m => m || h.last_model || '');
    } catch { setHistory(null); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => {
    setHistory(null);
    if (stockMode === 'stock' && stock && stock.length === stockLen(stock, stockDigitLen)) {
      lookup(stock);
      setEditing(e => (e === 0 ? null : e));
    }
    // eslint-disable-next-line
  }, [stock, stockMode, stockDigitLen]);

  const togglePart = (k) => setParts(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const toggleAllParts = () => setParts(prev => prev.size === TOTAL_PARTS ? new Set() : new Set(BUFFING_PARTS.map(p => p.key)));
  const toggleEmp = (id) => setEmployeeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  /* ---- progressive-disclosure state machine --------------------------- */
  const done = [
    stockMode === 'stock' ? (!!stock && stock.length === stockLen(stock, stockDigitLen)) : plateDone,
    !!model.trim(),
    scopeDone && parts.size > 0 && !!pct,
    authDone && !!authorizedBy.trim(),
    detailerDone && employeeIds.length > 0,
  ];
  const total = done.length;
  let current = done.findIndex(x => !x); if (current === -1) current = total;
  const openIdx = editing != null ? editing : current;
  const doneCount = done.filter(Boolean).length;
  const allDone = doneCount === total;
  const isOpen = (i) => openIdx === i;
  const isDone = (i) => done[i] && openIdx !== i;
  const isLocked = (i) => !done[i] && openIdx !== i && i > current;
  const editSetter = (i) => () => setEditing(i);
  const pickModel = (m) => { setModel(m); setEditing(e => (e === 1 ? null : e)); };

  const canSubmit = allDone && !loading;
  const partsLabels = BUFFING_PARTS.filter(p => parts.has(p.key)).map(p => p.label);
  const scopeText = allParts ? 'ALL CAR' : partsLabels.join(', ');

  async function submit() {
    if (!canSubmit) { setError('Complete all sections first.'); return; }
    setError(''); setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const dept = DEPT_FROM_BRAND[brand] || 'SERVICE';

      // 1) Authorization request (email approval flow).
      await api.submitBuffing({
        date: today, department: dept, stock_number: stock.trim(),
        car_model: model.trim(), authorized_by: authorizedBy.trim(),
        scope: `${scopeText} · ${pct}%`,
      });

      // 2) Lançamento — BUFFING job at the computed %.
      await api.submitJob({
        date: today, stock_number: stock.trim(), model: model.trim(),
        job_description: 'BUFFING', units: pct / 100, comments: [`BUFFING ${pct}%`, scopeText],
        extra_jobs: [], damage_defects: false, employee_ids: employeeIds,
        department: brand || undefined,
      });

      setSuccess(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setStockMode('stock'); setStock(''); setPlateDone(false); setModel(''); setHistory(null);
    setParts(new Set()); setPct(null); setScopeDone(false); setAuthorizedBy('NOAH DAVIES'); setAuthDone(false);
    setEmployeeIds(user?.type === 'admin' ? [] : [user?.id]); setDetailerDone(false);
    setEditing(null); setSuccess(false); setError('');
  }

  if (success) {
    return (
      <div style={{ background: PAGE, minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <span style={{ width: 64, height: 64, borderRadius: '50%', background: CHECK, color: '#fff', fontSize: 34, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
        <h2 style={{ color: '#1a1a2e', margin: '16px 0 4px', fontSize: 22, fontWeight: 800 }}>Buffing submitted!</h2>
        <p style={{ color: G500, margin: 0 }}>Authorization sent · logged at {pct}% ⏳</p>
        <p style={{ color: G400, fontSize: 13, margin: '6px 0 0' }}>{model} · {stock} · {scopeText}</p>
        <button onClick={reset} style={{ marginTop: 20, background: TEAL, color: '#fff', border: 'none', borderRadius: 12, padding: '12px 22px', fontWeight: 800, cursor: 'pointer' }}>Submit another</button>
      </div>
    );
  }

  const sums = [
    stock.toUpperCase(),
    model.trim().toUpperCase(),
    `${allParts ? 'ALL CAR' : `${parts.size} part${parts.size === 1 ? '' : 's'}`} · ${pct ?? '—'}%`,
    authorizedBy.trim().toUpperCase(),
    employeeIds.map(id => (detailers.find(c => c.id === id) || {}).name).filter(Boolean).join(', '),
  ];

  return (
    <div style={{ background: PAGE, minHeight: '100vh' }}>
      <FormHeader title="Buffing" doneCount={doneCount} total={total} />

      <div style={{ maxWidth: 620, margin: '0 auto', padding: '10px 0 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* 1 — IDENTIFICATION */}
          <SectionShell n="1" title="IDENTIFICATION" open={isOpen(0)} done={isDone(0)} locked={isLocked(0)} summary={sums[0]} onEdit={editSetter(0)}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <span onClick={() => { if (stockMode !== 'stock') { setStockMode('stock'); setStock(''); setPlateDone(false); setHistory(null); } }} style={chip(stockMode === 'stock', true)}>Stock Number</span>
              <span onClick={() => { if (stockMode !== 'plate') { setStockMode('plate'); setStock(''); setPlateDone(false); setHistory(null); } }} style={chip(stockMode === 'plate', true)}>Plate</span>
            </div>
            {stockMode === 'stock' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <SegmentedStock value={stock} onChange={setStock} onComplete={(v) => lookup(v)} digitLen={stockDigitLen} />
                  {brand && BRAND_LOGO_MAP[brand] && (
                    <img src={`${PUB2}/${BRAND_LOGO_MAP[brand]}`} alt={brand} style={{ width: 44, height: 32, objectFit: 'contain', flex: 'none' }} />
                  )}
                </div>
                {/^\d/.test(stock) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500 }}>DIGITS</span>
                    <span onClick={() => { setStockDigitLen(6); setStock(s => s.slice(0, 6)); }} style={chip(stockDigitLen === 6)}>Suzuki · 6</span>
                    <span onClick={() => { setStockDigitLen(5); setStock(s => s.slice(0, 5)); }} style={chip(stockDigitLen === 5)}>Used · 5</span>
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: G400, marginTop: 10 }}>Tap and type — first box is a letter (K / G / A sets the brand). A digit means Suzuki (6) or a used car (5).</div>
              </>
            ) : (
              <>
                <input value={stock} onChange={e => setStock(e.target.value.toUpperCase())} placeholder="e.g. 1ABC234"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${stock ? TEAL : LINE}`, borderRadius: 10, padding: '11px 13px', outline: 'none', fontSize: 18, fontWeight: 800, letterSpacing: 1.5, color: NAVY, fontFamily: MONO, textTransform: 'uppercase' }} />
                <button onClick={() => { if (stock.trim().length >= 3) { setPlateDone(true); setEditing(e => (e === 0 ? null : e)); lookup(stock); } }} disabled={stock.trim().length < 3}
                  style={{ marginTop: 12, height: 38, padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', cursor: stock.trim().length >= 3 ? 'pointer' : 'not-allowed', background: stock.trim().length >= 3 ? TEAL : TRACK, ...(stock.trim().length < 3 ? { color: G400 } : {}) }}>Continue</button>
              </>
            )}
            {histLoading && <div style={{ fontSize: 12, color: G400, marginTop: 10 }}>Checking history…</div>}
          </SectionShell>

          {/* Car history (stays visible after section 1 collapses) */}
          {history && !histLoading && !history.is_new && (
            <div style={{ background: '#fff', padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 8 }}>🕑 CAR HISTORY ({history.count} service{history.count === 1 ? '' : 's'})</div>
              {history.history.slice(0, 5).map((h, i) => (
                <div key={i} style={{ fontSize: 12.5, color: '#334155', display: 'flex', justifyContent: 'space-between', marginTop: i ? 5 : 0 }}>
                  <span>{fmtDMY(h.date)} · {h.service}</span><span style={{ color: G400 }}>{h.department || '—'}</span>
                </div>
              ))}
            </div>
          )}

          {/* 2 — CAR (model) */}
          <SectionShell n="2" title="CAR" open={isOpen(1)} done={isDone(1)} locked={isLocked(1)} summary={sums[1]} onEdit={editSetter(1)}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 8 }}>MODEL</div>
            {brandModels.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {brandModels.map(m => <span key={m} onClick={() => pickModel(m)} style={chip(model === m)}>{m}</span>)}
              </div>
            )}
            <input value={model} onChange={e => setModel(e.target.value)} placeholder="Model"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 14, outline: 'none' }} />
          </SectionShell>

          {/* 3 — BUFFING SCOPE (weighted part selector; the Claude Design diagram
              replaces this chip grid later) */}
          <SectionShell n="3" title="BUFFING SCOPE" open={isOpen(2)} done={isDone(2)} locked={isLocked(2)} summary={sums[2]} onEdit={editSetter(2)}>
            {/* What was buffed — the record that goes to the dealership. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: G400 }}>WHAT WAS BUFFED</span>
              <span onClick={toggleAllParts} style={chip(allParts, true)}>All car</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {BUFFING_PARTS.map(p => (
                <span key={p.key} onClick={() => togglePart(p.key)}
                  style={{ ...chip(parts.has(p.key)), padding: '11px 12px', fontSize: 12, textAlign: 'center' }}>
                  {p.label}
                </span>
              ))}
            </div>

            {/* And what it was worth. Chosen, not added up: the same two panels
                are a different job on a big car than on a small one. */}
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: G400, margin: '18px 0 10px' }}>
              HOW MUCH OF THE CAR
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiers.length}, 1fr)`, gap: 8 }}>
              {tiers.map(t => (
                <span key={t} onClick={() => setPct(t)}
                  style={{ ...chip(pct === t), padding: '14px 0', fontSize: 16, fontWeight: 800, textAlign: 'center' }}>
                  {t}%
                </span>
              ))}
            </div>

            <button onClick={() => { if (parts.size && pct) { setScopeDone(true); setEditing(e => (e === 2 ? null : e)); } }} disabled={!parts.size || !pct}
              style={{ marginTop: 16, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: (parts.size && pct) ? TEAL : TRACK, cursor: (parts.size && pct) ? 'pointer' : 'not-allowed', ...((parts.size && pct) ? {} : { color: G400 }) }}>
              {!parts.size ? 'Pick what was buffed' : !pct ? 'Pick a percentage' : 'Continue'}
            </button>
          </SectionShell>

          {/* 4 — AUTHORIZED BY */}
          <SectionShell n="4" title="AUTHORIZED BY" open={isOpen(3)} done={isDone(3)} locked={isLocked(3)} summary={sums[3]} onEdit={editSetter(3)}>
            <input value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)} placeholder="Authorized by"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 14, outline: 'none', textTransform: 'uppercase' }} />
            <button onClick={() => { if (authorizedBy.trim()) { setAuthDone(true); setEditing(e => (e === 3 ? null : e)); } }} disabled={!authorizedBy.trim()}
              style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: authorizedBy.trim() ? TEAL : TRACK, cursor: authorizedBy.trim() ? 'pointer' : 'not-allowed', ...(authorizedBy.trim() ? {} : { color: G400 }) }}>Continue</button>
          </SectionShell>

          {/* 5 — DETAILING BY */}
          <SectionShell n="5" title="DETAILING BY" open={isOpen(4)} done={isDone(4)} locked={isLocked(4)} summary={sums[4]} onEdit={editSetter(4)}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {detailers.map(c => <span key={c.id} onClick={() => toggleEmp(c.id)} style={chip(employeeIds.includes(c.id))}>{c.name}</span>)}
            </div>
            <button onClick={() => { if (employeeIds.length) { setDetailerDone(true); setEditing(e => (e === 4 ? null : e)); } }} disabled={!employeeIds.length}
              style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: employeeIds.length ? TEAL : TRACK, cursor: employeeIds.length ? 'pointer' : 'not-allowed', ...(employeeIds.length ? {} : { color: G400 }) }}>Continue</button>
          </SectionShell>

          {/* SUMMARY */}
          {allDone && (
            <div style={{ background: '#fff', padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, marginBottom: 12 }}>SUMMARY</div>
              <div style={{ background: PAGE, borderRadius: 12, padding: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {brand && BRAND_LOGO_MAP[brand] && <img src={`${PUB2}/${BRAND_LOGO_MAP[brand]}`} alt={brand} style={{ width: 46, height: 42, objectFit: 'contain', flex: 'none' }} />}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>{model.trim().toUpperCase()}</div>
                  <div style={{ fontSize: 12, color: G500, fontFamily: MONO }}>{stock.toUpperCase()} · {sums[4]}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, padding: '5px 11px', borderRadius: 999, background: TEAL_TINT, color: TEAL }}>BUFFING {pct}%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 12px', borderRadius: 8, fontSize: 13, margin: '10px 16px 0' }}>{error}</div>}

        <div style={{ position: 'sticky', bottom: 8, padding: '12px 16px 0', marginTop: 10 }}>
          <button onClick={submit} disabled={!canSubmit}
            style={{ width: '100%', height: 50, borderRadius: 14, border: 'none', fontSize: 15, fontWeight: 800, color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', background: canSubmit ? GREEN : G300, boxShadow: canSubmit ? '0 6px 18px rgba(5,150,105,.32)' : 'none' }}>
            {loading ? 'Submitting…' : (allDone ? `Submit Buffing · ${pct}%` : `Submit Buffing · ${total - doneCount} to go`)}
          </button>
        </div>
      </div>
    </div>
  );
}
